import React from 'react';
import {
  Box,
  Flex,
  keyframes,
  Skeleton,
  SkeletonText,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(0.92); }
  50% { opacity: 1; transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

/** Branded amber ring spinner used by all loaders. */
export function BrandSpinner({ size = 48, label }) {
  const ring = Math.max(size * 0.12, 3);
  return (
    <VStack spacing={4} role="status" aria-live="polite" aria-busy="true">
      <Box position="relative" w={`${size}px`} h={`${size}px`}>
        <Box
          position="absolute"
          inset={0}
          borderRadius="full"
          border={`${ring}px solid`}
          borderColor="bg.amberTint"
        />
        <Box
          position="absolute"
          inset={0}
          borderRadius="full"
          border={`${ring}px solid transparent`}
          borderTopColor="utkarsh.amber"
          borderRightColor="utkarsh.amber"
          animation={`${spin} 0.85s linear infinite`}
        />
        <Flex
          position="absolute"
          inset="18%"
          align="center"
          justify="center"
          borderRadius="full"
          bg="bg.amberTint"
          animation={`${pulse} 1.4s ease-in-out infinite`}
        >
          <Text
            fontSize={`${Math.max(size * 0.28, 12)}px`}
            fontWeight="800"
            color="utkarsh.ink"
            letterSpacing="-0.02em"
            lineHeight="1"
          >
            U
          </Text>
        </Flex>
      </Box>
      {label ? (
        <Text
          fontSize="16px"
          fontWeight="600"
          color="text.secondary"
          letterSpacing="0.01em"
        >
          {label}
        </Text>
      ) : null}
    </VStack>
  );
}

/** Full-area page / detail loader. */
export function PageLoader({
  label = 'Loading…',
  minH = '420px',
  py = 20,
  ...props
}) {
  return (
    <Flex
      justify="center"
      align="center"
      minH={minH}
      py={py}
      w="100%"
      {...props}
    >
      <BrandSpinner size={72} label={label} />
    </Flex>
  );
}

/** Full-viewport loader while auth / bootstrap resolves. */
export function AuthLoader({ label = 'Loading Utkarsh…' }) {
  return (
    <Flex
      minH="100vh"
      w="100%"
      align="center"
      justify="center"
      bg="bg.canvas"
      px={6}
    >
      <VStack spacing={7}>
        <BrandSpinner size={88} />
        <Box textAlign="center">
          <Text fontSize="22px" fontWeight="700" color="text.primary">
            Utkarsh Admin
          </Text>
          <Text mt={2} fontSize="16px" color="text.secondary">
            {label}
          </Text>
        </Box>
        <Box
          w="180px"
          h="4px"
          borderRadius="full"
          overflow="hidden"
          bg="bg.input"
        >
          <Box
            h="100%"
            w="40%"
            borderRadius="full"
            bg="utkarsh.amber"
            backgroundImage="linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)"
            backgroundSize="200% 100%"
            animation={`${shimmer} 1.2s ease-in-out infinite`}
          />
        </Box>
      </VStack>
    </Flex>
  );
}

/** Skeleton placeholder for list / table cards. */
export function TableLoader({ rows = 6, columns = 5, showHeader = true }) {
  return (
    <Box px={{ base: 5, xl: 6 }} py={{ base: 6, xl: 8 }} minH="320px">
      {showHeader ? (
        <Skeleton
          h="18px"
          w="160px"
          mb={6}
          borderRadius="md"
          startColor="bg.input"
          endColor="border.subtle"
        />
      ) : null}
      <VStack spacing={4} align="stretch">
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <Flex
            key={rowIdx}
            gap={5}
            align="center"
            py={4}
            px={4}
            borderRadius="md"
            bg={rowIdx % 2 === 0 ? 'bg.input' : 'transparent'}
          >
            {Array.from({ length: columns }).map((__, colIdx) => (
              <Skeleton
                key={colIdx}
                flex={colIdx === 1 ? 1.4 : 1}
                h="16px"
                borderRadius="md"
                startColor="bg.surface"
                endColor="border.subtle"
                opacity={0.95 - rowIdx * 0.05}
              />
            ))}
          </Flex>
        ))}
      </VStack>
    </Box>
  );
}

/** Skeleton cards for dashboard / summary grids. */
export function DashboardLoader({ cards = 3 }) {
  return (
    <Box>
      <Skeleton h="32px" w="180px" mb={2} borderRadius="md" startColor="bg.input" endColor="border.subtle" />
      <SkeletonText noOfLines={1} w="360px" maxW="100%" mb={6} skeletonHeight="14px" startColor="bg.input" endColor="border.subtle" />
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        {Array.from({ length: cards }).map((_, i) => (
          <Box
            key={i}
            bg="bg.surface"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            p={5}
          >
            <Skeleton h="12px" w="90px" mb={4} borderRadius="md" startColor="bg.input" endColor="border.subtle" />
            <Skeleton h="36px" w="72px" mb={3} borderRadius="md" startColor="bg.input" endColor="border.subtle" />
            <Skeleton h="20px" w="100px" borderRadius="full" startColor="bg.amberTint" endColor="bg.input" />
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

/** Compact inline loader (filters refresh, small panels). */
export function InlineLoader({ label = 'Loading…', py = 12 }) {
  return (
    <Flex justify="center" align="center" py={py} w="100%" minH="180px">
      <BrandSpinner size={56} label={label} />
    </Flex>
  );
}
